<template>
    <div class="flex items-center justify-between w-full">
        <!-- Left side: Icon and label -->
        <div class="flex items-center gap-1.5 min-w-0">
            <!-- File type icon -->
            <component :is="fileIcon" class="h-3.5 w-3.5 shrink-0" :class="isActive ? 'text-primary-foreground' : 'text-muted-foreground'" />

            <!-- Tab label -->
            <div class="flex items-center gap-1.5">
                <span
                    class="text-xs truncate"
                    :class="[isActive ? 'text-primary-foreground' : 'text-muted-foreground', tab.primary ? 'font-bold' : 'font-medium']">
                    {{ displayLabel }}
                </span>
                <BadgeCheck v-if="tab.primary" class="h-4 w-4 shrink-0 text-accent-foreground" />
            </div>

            <!-- Enhanced modified indicator -->
            <div
                v-if="isModified"
                class="h-2 w-2 rounded-full shrink-0 transition-colors"
                :class="modifiedIndicatorClass"
                :title="modifiedTooltipText"
                :aria-label="modifiedAriaLabel" />
        </div>

        <!-- Right side: Info icon -->
        <div class="flex items-center gap-1">
            <!-- Transform info tooltip -->
            <HoverCard :openDelay="300" :closeDelay="100">
                <HoverCardTrigger as-child>
                    <button
                        type="button"
                        class="h-3.5 w-3.5 shrink-0 rounded-sm opacity-60 hover:opacity-100 transition-opacity focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
                        :aria-label="`View transform details for ${displayLabel}`">
                        <Info class="h-3 w-3" />
                    </button>
                </HoverCardTrigger>
                <HoverCardContent class="w-96 p-4" side="bottom" align="start" :sideOffset="4">
                    <div class="space-y-3">
                        <div>
                            <h4 class="text-sm font-semibold text-foreground">{{ transformDetails.title }}</h4>
                            <p class="text-xs text-muted-foreground mt-1">{{ transformDetails.description }}</p>
                        </div>

                        <div>
                            <h5 class="text-xs font-medium text-foreground mb-1">Function Signature:</h5>
                            <code class="text-xs bg-muted/50 rounded px-2 py-1 font-mono block break-all">
                                {{ transformDetails.signature }}
                            </code>
                        </div>

                        <div v-if="transformDetails.returns">
                            <h5 class="text-xs font-medium text-foreground mb-1">Returns:</h5>
                            <p class="text-xs text-muted-foreground">{{ transformDetails.returns }}</p>
                        </div>
                    </div>
                </HoverCardContent>
            </HoverCard>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { FileCode, FileText, Key, Database, Share2, Braces, Info, BadgeCheck } from 'lucide-vue-next';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { getTransformTooltip } from '@/utils/transform-tooltips';

const props = defineProps({
    tab: {
        type: Object,
        required: true,
        validator: (tab) => tab.id && tab.label && tab.type
    },
    isActive: {
        type: Boolean,
        default: false
    },
    isModified: {
        type: Boolean,
        default: false
    }
});

// File type icon mapping
const fileIcon = computed(() => {
    const iconMap = {
        COMMON_TRANSFORM: FileCode,
        VALUE_TRANSFORM: FileText,
        KEY_TRANSFORM: Key,
        TOPIC_TRANSFORM: Share2,
        VALUE_SCHEMA_TRANSFORM: Database,
        KEY_SCHEMA_TRANSFORM: Braces
    };

    return iconMap[props.tab.type] || FileCode;
});

// Display label - full file names for clarity
const displayLabel = computed(() => {
    const labelMap = {
        COMMON_TRANSFORM: 'Common Transform',
        VALUE_TRANSFORM: 'Value Transform',
        KEY_TRANSFORM: 'Key Transform',
        TOPIC_TRANSFORM: 'Topic Transform',
        VALUE_SCHEMA_TRANSFORM: 'Value Schema Transform',
        KEY_SCHEMA_TRANSFORM: 'Key Schema Transform'
    };

    return labelMap[props.tab.type] || props.tab.label;
});

// Transform function details for tooltip
const transformDetails = computed(() => {
    // If tab has custom tooltip data, use that
    if (props.tab.tooltip) {
        return props.tab.tooltip;
    }

    // Otherwise, try to get transform-specific tooltip
    return getTransformTooltip(props.tab.type, props.tab.label);
});

// Enhanced modified indicator styling
const modifiedIndicatorClass = computed(() => {
    if (!props.isModified) return '';

    return props.isActive
        ? 'bg-green-500 shadow-sm shadow-green-500/25' // Active and modified - green
        : 'bg-blue-500 shadow-sm shadow-blue-500/25'; // Inactive and modified - blue
});

// Modified indicator tooltip text
const modifiedTooltipText = computed(() => {
    if (!props.isModified) return '';

    return props.isActive ? 'Active tab with unsaved changes' : 'Inactive tab with unsaved changes';
});

// Modified indicator ARIA label
const modifiedAriaLabel = computed(() => {
    if (!props.isModified) return '';

    return props.isActive ? 'Current tab has unsaved changes' : 'Tab has unsaved changes';
});
</script>
